<script lang="ts">
  import { api } from "../api/client";
  import Button from "../components/Button.svelte";
  import FileInput from "../components/FileInput.svelte";
  import Input from "../components/Input.svelte";
  import Text from "../components/Text.svelte";
  import Textarea from "../components/Textarea.svelte";
  import { selectedBuyer } from "../stores/buyer";

  type FormData = {
    subject: string;
    message: string;
    files: File[];
    error: string;
    success: string;
    loading: boolean;
  };

  let form: FormData = {
    subject: "",
    message: "",
    files: [],
    error: "",
    success: "",
    loading: false,
  };

  async function sendForm() {
    if (!$selectedBuyer) {
      form.error = "No buyer are currently selected, please contact us at support@rekki.com";
      return;
    }

    form.loading = true;

    const formData = new FormData();
    formData.append("subject", form.subject);
    formData.append("message", form.message);
    formData.append("buyer_id", $selectedBuyer.id);

    for (let i = 0; i < form.files.length; i++) {
      formData.append("files", form.files[i]);
    }

    try {
      await api.post("contact-us", { body: formData }).json();

      form.success =
        "We will reply via email within one business day, or faster for urgent issues.";
      form.error = "";
    } catch (e) {
      form.success = "";
      form.error =
        "Something went wrong while sending your message. Please try again later or email us at support@rekki.com.";
    }

    form.loading = false;
  }

  $: formIsValid = form.subject.trim() !== "" && form.message.trim() !== "";
  $: formIsSuccess = form.success !== "";
</script>

<div class="container">
  <div class="header">
    <h1>Contact us</h1>
    <Text>
      Let us know how we can help. We will aim to reply within one business day, or faster for
      urgent issues.
    </Text>

    <div class="input-container">
      <Input
        placeholder="Subject of your message"
        label="Subject"
        bind:value={form.subject}
        errorMessage="Please specify a subject"
        required
        disabled={formIsSuccess}
      />
    </div>

    <div class="input-container">
      <Textarea
        placeholder="Include as many details as you can so we can help you as quickly as possible"
        label="Message"
        bind:value={form.message}
        errorMessage="Please specify a message"
        required
        disabled={formIsSuccess}
      />
    </div>

    {#if !formIsSuccess}
      <div class="input-container">
        <FileInput
          id="contact-files-upload"
          maxSize={10 * 1024 * 1024}
          label="Upload images along with the message if needed"
          accept=".jpg, .jpeg, .png"
          files={form.files}
          multiple
          onFilesSelected={(files) => (form.files = files)}
        />
      </div>
    {/if}
    {#if formIsSuccess}
      <Text>{form.files.map((f) => f.name).join(", ")}</Text>
    {/if}

    {#if form.error}
      <div class="error">
        <Text size="medium-large">{form.error}</Text>
      </div>
    {/if}

    {#if formIsSuccess}
      <div class="success">
        <Text>{form.success}</Text>
      </div>
    {/if}

    {#if !formIsSuccess}
      <div class="btn-container">
        <div class="btn">
          <Button disabled={!formIsValid || form.loading} stretch={true} on:click={sendForm}
            >Send</Button
          >
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .container {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: white;
    height: 100vh;
    overflow: scroll;
    justify-content: space-between;
  }
  .header {
    margin-bottom: 36px;
  }
  h1 {
    font-size: 36px;
  }
  .input-container {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .btn-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
  .btn {
    width: 200px;
  }
  .error {
    padding: 18px;
    border-radius: 8px;
    color: var(--rekki-light);
  }
  .success {
    padding: 18px;
    border-radius: 8px;
    color: var(--rekki-light);
    margin-top: 24px;
    margin-bottom: 24px;
  }
</style>
